import { useEffect, useRef, useState, type ChangeEvent, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { ApiError } from '../api/client';
import {
  createCustomerNote,
  deleteCustomerNote,
  fetchNotePhoto,
  type NotePhotoUpload,
} from '../api/endpoints/customerFiles';
import { createIdempotencyKeyHolder } from '../api/idempotency';
import type { CustomerFile, CustomerNote } from '../api/schemas';
import { MAX_NOTE_LENGTH, noteWhen, type FileVocabulary } from '../lib/customerFile';
import { buildVisionImageUpload } from '../lib/photoPipeline';
import { customerFileKey, storeNotes } from '../lib/useCustomerProfiles';

type PendingPhoto = NotePhotoUpload & { previewUrl: string };

/** An object URL for a Blob that lives exactly as long as the component using it. */
function useObjectUrl(blob: Blob | undefined): string | undefined {
  const [url, setUrl] = useState<string>();
  useEffect(() => {
    if (!blob) return undefined;
    const next = URL.createObjectURL(blob);
    setUrl(next);
    return () => URL.revokeObjectURL(next);
  }, [blob]);
  return url;
}

export function CustomerNotes({
  customerId,
  notes,
  vocabulary,
}: {
  customerId: string;
  notes: CustomerNote[];
  vocabulary: FileVocabulary;
}) {
  const now = new Date();
  // Only the note this person just wrote lands with motion; opening a file with
  // twenty notes should not animate twenty rows.
  const [freshId, setFreshId] = useState<string>();
  return (
    <div className="customer-notes">
      <p className="customer-file-label">
        Notas{notes.length > 0 ? <span className="customer-notes-count"> · {notes.length}</span> : null}
      </p>
      <NoteComposer customerId={customerId} vocabulary={vocabulary} onCreated={setFreshId} />
      {notes.length === 0 ? (
        <p className="row-sub customer-notes-empty">Todavía no hay notas. Lo que escribas aquí lo ve el resto del equipo.</p>
      ) : (
        <ol className="customer-note-list">
          {notes.map((note) => (
            <NoteItem key={note.id} customerId={customerId} note={note} now={now} fresh={note.id === freshId} />
          ))}
        </ol>
      )}
    </div>
  );
}

function NoteComposer({
  customerId,
  vocabulary,
  onCreated,
}: {
  customerId: string;
  vocabulary: FileVocabulary;
  onCreated: (noteId: string) => void;
}) {
  const queryClient = useQueryClient();
  const createKey = useRef(createIdempotencyKeyHolder()).current;
  const fileInput = useRef<HTMLInputElement>(null);
  const [body, setBody] = useState('');
  const [photo, setPhoto] = useState<PendingPhoto>();
  const [photoError, setPhotoError] = useState<string>();
  const [readingPhoto, setReadingPhoto] = useState(false);

  useEffect(() => () => (photo ? URL.revokeObjectURL(photo.previewUrl) : undefined), [photo]);

  const create = useMutation({
    mutationFn: () =>
      createCustomerNote(
        customerId,
        { body: body.trim(), ...(photo ? { photo: { blob: photo.blob, width: photo.width, height: photo.height } } : {}) },
        createKey.ensure(),
      ),
    onSuccess: (note) => {
      const current = queryClient.getQueryData<CustomerFile>(customerFileKey(customerId));
      const others = (current?.notes ?? []).filter((entry) => entry.id !== note.id);
      storeNotes(queryClient, customerId, [note, ...others], 1);
      onCreated(note.id);
      createKey.reset();
      setBody('');
      setPhoto(undefined);
    },
  });

  const pickPhoto = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    setPhotoError(undefined);
    setReadingPhoto(true);
    try {
      const upload = await buildVisionImageUpload(file);
      setPhoto({ ...upload, previewUrl: URL.createObjectURL(upload.blob) });
      createKey.reset();
    } catch {
      setPhotoError('No pudimos leer esa foto. Prueba con otra.');
    } finally {
      setReadingPhoto(false);
    }
  };

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if ((!body.trim() && !photo) || create.isPending) return;
    create.mutate();
  };

  const canSave = Boolean(body.trim() || photo) && !create.isPending && !readingPhoto;

  return (
    <form className="customer-note-composer" onSubmit={submit}>
      <label className="sr-only" htmlFor={`note-${customerId}`}>
        Nota interna
      </label>
      <textarea
        id={`note-${customerId}`}
        rows={3}
        maxLength={MAX_NOTE_LENGTH}
        placeholder={vocabulary.notePlaceholder}
        value={body}
        onChange={(event) => {
          setBody(event.target.value);
          createKey.reset();
        }}
      />
      {photo ? (
        <div className="customer-note-preview">
          <img src={photo.previewUrl} alt="Foto que se adjunta a la nota" />
          <button type="button" className="btn small ghost" onClick={() => setPhoto(undefined)}>
            Quitar foto
          </button>
        </div>
      ) : null}
      {photoError ? <p className="field-error">{photoError}</p> : null}
      {create.isError ? <p className="field-error" role="alert">{saveMessage(create.error)}</p> : null}
      <div className="customer-note-actions">
        <input
          ref={fileInput}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          className="sr-only"
          tabIndex={-1}
          onChange={(event) => void pickPhoto(event)}
        />
        <button
          type="button"
          className="btn small ghost customer-note-photo"
          disabled={readingPhoto || create.isPending}
          onClick={() => fileInput.current?.click()}
        >
          <svg viewBox="0 0 20 20" aria-hidden="true">
            <path d="M3.5 6.5h2.2l1.4-2h5.8l1.4 2h2.2a1 1 0 0 1 1 1v7.5a1 1 0 0 1-1 1H3.5a1 1 0 0 1-1-1V7.5a1 1 0 0 1 1-1Z" />
            <circle cx="10" cy="11" r="3" />
          </svg>
          {readingPhoto ? 'Preparando…' : photo ? 'Cambiar foto' : vocabulary.photoLabel}
        </button>
        <button type="submit" className="btn small" disabled={!canSave}>
          {create.isPending ? 'Guardando…' : 'Guardar nota'}
        </button>
      </div>
    </form>
  );
}

function NoteItem({
  customerId,
  note,
  now,
  fresh,
}: {
  customerId: string;
  note: CustomerNote;
  now: Date;
  fresh: boolean;
}) {
  const queryClient = useQueryClient();
  const deleteKey = useRef(createIdempotencyKeyHolder()).current;
  const remove = useMutation({
    mutationFn: () => deleteCustomerNote(customerId, note.id, deleteKey.ensure()),
    onSuccess: () => {
      const current = queryClient.getQueryData<CustomerFile>(customerFileKey(customerId));
      storeNotes(queryClient, customerId, (current?.notes ?? []).filter((entry) => entry.id !== note.id), -1);
    },
    onSettled: () => deleteKey.reset(),
  });

  return (
    <li className="customer-note" data-fresh={fresh || undefined}>
      <p className="customer-note-meta">
        <span className="customer-note-author">{note.authorName}</span>
        <time dateTime={note.createdAt}>{noteWhen(note.createdAt, now)}</time>
      </p>
      {note.body ? <p className="customer-note-body">{note.body}</p> : null}
      {note.photo ? <NotePhoto url={note.photo.url} width={note.photo.width} height={note.photo.height} /> : null}
      {remove.isError ? <p className="field-error" role="alert">{saveMessage(remove.error)}</p> : null}
      {note.canDelete ? <DeleteNote pending={remove.isPending} onConfirm={() => remove.mutate()} /> : null}
    </li>
  );
}

/** Quiet until asked: a ghost "Borrar" that turns into a red confirm, never a red button per note. */
function DeleteNote({ pending, onConfirm }: { pending: boolean; onConfirm: () => void }) {
  const [confirming, setConfirming] = useState(false);
  if (pending) return <p className="row-sub customer-note-delete">Borrando…</p>;
  if (!confirming) {
    return (
      <div className="customer-note-delete">
        <button type="button" className="btn small ghost" onClick={() => setConfirming(true)}>
          Borrar
        </button>
      </div>
    );
  }
  return (
    <div className="customer-note-delete action-pair">
      <button
        type="button"
        className="btn small danger"
        onClick={() => {
          setConfirming(false);
          onConfirm();
        }}
      >
        Sí, borrar
      </button>
      <button type="button" className="btn small ghost" onClick={() => setConfirming(false)}>
        Cancelar
      </button>
    </div>
  );
}

function NotePhoto({ url, width, height }: { url: string; width: number; height: number }) {
  const [expanded, setExpanded] = useState(false);
  const photo = useQuery({
    queryKey: ['note-photo', url],
    queryFn: () => fetchNotePhoto(url),
    staleTime: Infinity,
    retry: 1,
  });
  const objectUrl = useObjectUrl(photo.data);

  if (photo.isError) return <p className="row-sub">No pudimos mostrar la foto.</p>;
  return (
    <button
      type="button"
      className="customer-note-photo-frame"
      data-expanded={expanded || undefined}
      style={{ aspectRatio: `${width} / ${height}` }}
      aria-label={expanded ? 'Achicar foto' : 'Ver foto completa'}
      onClick={() => setExpanded((value) => !value)}
    >
      {objectUrl ? <img src={objectUrl} alt="Foto de la nota" /> : null}
    </button>
  );
}

function saveMessage(error: unknown): string {
  if (error instanceof ApiError) {
    const field = error.fields ? Object.values(error.fields)[0] : undefined;
    return field ?? error.message;
  }
  return 'No se pudo guardar. Revisa tu conexión e intenta de nuevo.';
}
